import "core-js/modules/es.array.concat";
import "core-js/modules/es.array.map";
import "core-js/modules/es.array.sort";
import "core-js/modules/es.object.to-string";
import "core-js/modules/es.promise";
import _extends from "@babel/runtime/helpers/esm/extends";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import invariant from 'invariant';
import AuthContext from './AuthContext';
import AuthRedirectContext from './AuthRedirectContext';
import createAuthContextInitializator from './createAuthContextInitializator';
import createAuthenticatePipe from './createAuthenticatePipe';
import createLogoutPipe from './createLogoutPipe';
import createAuthStore from './createAuthStore';
var authenticateHandlers = [];
var logoutHandlers = [];
var authCallBack;
var onAuthenticatedPromise = new Promise(function (resolve) {
  authCallBack = function authCallBack() {
    return resolve(true);
  };
});
/**
 * Provider drzici informace o autentifikovanem uzivateli a dalsi metadata. A nabizi nastroje
 * pro prihlaseni a odhlaseni uzivatele.
 *
 * Context:
 * ```js
 * {
 *   accessToken,
 *   isAuthenticated,
 *   meta,
 *   user,
 *   scope,
 * }
 * ```
 *
 * @component
 */

function AuthProvider(_ref) {
  var parentAccessToken = _ref.accessToken,
      children = _ref.children,
      parentIsAuthenticated = _ref.isAuthenticated,
      loginPath = _ref.loginPath,
      parentMeta = _ref.meta,
      oauthOptions = _ref.oauthOptions,
      handleParentLogout = _ref.onLogout,
      render = _ref.render,
      parentScope = _ref.scope,
      storeKey = _ref.storeKey,
      useLocation = _ref.useLocation,
      parentUser = _ref.user,
      useRedirect = _ref.useRedirect;

  var _createAuthStore = createAuthStore(storeKey),
      _createAuthStore2 = _slicedToArray(_createAuthStore, 2),
      getStoredAuthData = _createAuthStore2[0],
      setStoredAuthData = _createAuthStore2[1];

  var _useState = useState(createAuthContextInitializator(getStoredAuthData, setStoredAuthData, authCallBack, {
    accessToken: parentAccessToken,
    isAuthenticated: parentIsAuthenticated,
    meta: parentMeta,
    scope: parentScope,
    user: parentUser
  })),
      _useState2 = _slicedToArray(_useState, 2),
      ctx = _useState2[0],
      setCtx = _useState2[1];

  invariant(children || render, 'AuthProvider require one of children or render props to be set.');
  /**
   * Registuje handlery authentifikacni pipeline.
   */

  var registerOnAuthenticate = function registerOnAuthenticate(fn, order) {
    var _ref2 = Array.isArray(fn) ? fn : [fn, order],
        _ref3 = _slicedToArray(_ref2, 2),
        handler = _ref3[0],
        o = _ref3[1];

    var rOrder = o || authenticateHandlers.length + 100;
    authenticateHandlers = [].concat(_toConsumableArray(authenticateHandlers), [[handler, rOrder]]);
  };
  /**
   * Registuje handlery logout pipeline.
   */


  var registerOnLogout = function registerOnLogout(fn) {
    var fna = Array.isArray(fn) ? fn : [fn];
    logoutHandlers = [].concat(_toConsumableArray(logoutHandlers), _toConsumableArray(fna));
  };

  var logout = createLogoutPipe(setStoredAuthData, function () {
    return logoutHandlers;
  }, handleParentLogout, function (x) {
    return setCtx(x);
  });
  var authenticate = createAuthenticatePipe(setStoredAuthData, function () {
    return _toConsumableArray(authenticateHandlers).sort(function (a, b) {
      return a[1] - b[1];
    }).map(function (i) {
      return i[0];
    });
  }, logout, authCallBack, function (x) {
    return setCtx(x);
  });
  return /*#__PURE__*/React.createElement(AuthRedirectContext.Provider, {
    value: {
      useRedirect: useRedirect,
      useLocation: useLocation,
      loginPath: loginPath
    }
  }, /*#__PURE__*/React.createElement(AuthContext.Provider, {
    value: _extends({}, ctx, {
      onAuthenticated: onAuthenticatedPromise,
      oauthOptions: oauthOptions,
      authenticate: authenticate,
      handleLogout: logout,
      registerOnAuthenticate: registerOnAuthenticate,
      registerOnLogout: registerOnLogout
    })
  }, (children || render)({
    authenticate: authenticate,
    handleLogout: logout,
    oauthOptions: oauthOptions,
    accessToken: ctx.accessToken,
    registerOnAuthenticate: registerOnAuthenticate,
    registerOnLogout: registerOnLogout
  })));
}

AuthProvider.propTypes = {
  /**
   * Inicialni nastaveni access tokenu. Ocekavamy je JWT access token.
   *
   * **Doporucujeme pouziti pouze pro testovaci ucely**
   */
  accessToken: PropTypes.string,

  /**
   * Children func.
   *
   * Signatura funkce: `({ }) => Node`
   *
   * - `authenticate: (authContext) => Promise<{ user, meta }>` spusteni autentifinacni sekvence. Projde vsechni
   *     funkce registrovane pomoci registerOnAuthenticate. A nastavi atributy prihlaseni.
   * - `logout: () => void` spusteni odhlasovaci sekvence.
   * - `registerOnAuthenticate: ` - registrovani funkci do sekvence prihlaseni. Obvykle predavame
   *     do ConnectSrver, nebo do jinych komponent overujicich kredence autentizace.
   * - `registerOnLogout: ` - registrace funkci do sekvence odhlaseni. Obvykle predavame
   *     do ConnectSrver, nebo do jinych komponent overujicich kredence autentizace, ktere muzou
   *     bezpecne nastavit svuj stav.
   */
  children: PropTypes.func,

  /**
   * Inicialni nastaveni priznaku autentifikovanosti.
   *
   * **Doporucujeme pouziti pouze pro testovaci ucely**
   */
  isAuthenticated: PropTypes.bool,

  /**
   * Custom cesta k login page.
   */
  loginPath: PropTypes.string,

  /**
   * Inicialni metadata.
   *
   * **Doporucujeme pouziti pouze pro testovaci ucely**
   */
  meta: PropTypes.any,
  // eslint-disable-line react/forbid-prop-types

  /**
   * Nastaveni pro OAuth2 authentikaci a authorizaci.
   */
  oauthOptions: PropTypes.shape({
    appName: PropTypes.string,
    authorizeUrl: PropTypes.string.isRequired,
    clientId: PropTypes.string.isRequired,
    clientSecret: PropTypes.string,
    redirectUri: PropTypes.string.isRequired,
    tokenUrl: PropTypes.string
  }),

  /**
   * Handler, ktery bude spusten pri odhlaseni.
   *
   * Signatura: `() => Promise`
   */
  onLogout: PropTypes.func,

  /**
   * Children func.
   *
   * Signatura funkce: `({ }) => Node`
   *
   * - `authenticate: (authContext) => Promise<{ user, meta }>` spusteni autentifinacni sekvence. Projde vsechni
   *     funkce registrovane pomoci registerOnAuthenticate. A nastavi atributy prihlaseni.
   *     Uzivatelske kredence jsou ulozeny v properte `credentials` auth kontextu.
   * - `logout: () => void` spusteni odhlasovaci sekvence.
   * - `registerOnAuthenticate: ` - registrovani funkci do sekvence prihlaseni. Obvykle predavame
   *     do ConnectSrver, nebo do jinych komponent overujicich kredence autentizace.
   * - `registerOnLogout: ` - registrace funkci do sekvence odhlaseni. Obvykle predavame
   *     do ConnectSrver, nebo do jinych komponent overujicich kredence autentizace, ktere muzou
   *     bezpecne nastavit svuj stav.
   */
  render: PropTypes.func,
  scope: PropTypes.arrayOf(PropTypes.string),

  /**
   * Klic pod, kterym je access token uloz v local storage.
   */
  storeKey: PropTypes.string,

  /**
   * Inicialni uzivatel.
   *
   * **Doporucujeme pouziti pouze pro testovaci ucely**
   */
  useLocation: PropTypes.func.isRequired,

  /**
   * Hook pro ziskani router historie.
   */
  user: PropTypes.shape({
    firstName: PropTypes.string,
    id: PropTypes.string.isRequired,
    lastName: PropTypes.string,
    name: PropTypes.string.isRequired
  }),

  /**
   * Redirect hook pro customizaci routeru.
   */
  useRedirect: PropTypes.func.isRequired
};
AuthProvider.defaultProps = {
  accessToken: undefined,
  children: null,
  isAuthenticated: false,
  loginPath: '/login',
  meta: undefined,
  oauthOptions: null,
  onLogout: undefined,
  render: null,
  scope: [''],
  storeKey: 'auth-provider',
  user: null
};
export default AuthProvider;